import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { AlertTriangle, ImagePlus, X, Plus, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { createDirectoryContact, updateDirectoryContact, checkDirectoryDuplicates, createDirectoryTag } from "../../services/api";
import { resizeImage, displayName, PLACE_TYPES } from "./directoryUtils";
import { useAuth } from "../../context/AuthContext";

const EMPTY = {
  kind: "person", first_name: "", last_name: "", company_name: "", category_id: "", job: "",
  phone: "", email: "", address: "", city: "", website: "", socials: "", notes: "",
  availability: "", tags: [], photo: null, photos: [], links: [],
  place_type: "", on_site_contact: "", capacity_info: "", constraints: "",
};

export const ContactFormDialog = ({ open, onClose, contact, kind, categories, tags, onSaved, onTagsChanged }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [dups, setDups] = useState([]);
  const [dupIgnored, setDupIgnored] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [linkLabel, setLinkLabel] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const photoRef = useRef(null);
  const placePhotosRef = useRef(null);
  const isPlace = form.kind === "place";

  useEffect(() => {
    if (open) {
      setForm(contact ? { ...EMPTY, ...contact } : { ...EMPTY, kind: kind || "person" });
      setDups([]); setDupIgnored(false); setNewTag(""); setLinkLabel(""); setLinkUrl("");
    }
  }, [open, contact, kind]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const checkDups = async () => {
    const name = displayName(form);
    if (!name && !form.phone && !form.email) { setDups([]); return; }
    try {
      const res = await checkDirectoryDuplicates({
        name: name || undefined, phone: form.phone || undefined,
        email: form.email || undefined, exclude_id: contact?.id,
      });
      setDups(res.data.duplicates);
    } catch { /* silencieux */ }
  };

  const handlePhoto = async (e, multi) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    try {
      const urls = await Promise.all(files.map((f) => resizeImage(f)));
      if (multi) set("photos", [...(form.photos || []), ...urls]);
      else set("photo", urls[0]);
    } catch { toast.error("Impossible de lire l'image"); }
  };

  const toggleTag = (name) => {
    set("tags", form.tags.includes(name) ? form.tags.filter((t) => t !== name) : [...form.tags, name]);
  };

  const addTag = async () => {
    const name = newTag.trim();
    if (!name) return;
    try {
      await createDirectoryTag({ name });
      setNewTag("");
      onTagsChanged?.();
      set("tags", [...form.tags, name]);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const addLink = () => {
    if (!linkUrl.trim()) return;
    set("links", [...(form.links || []), { label: linkLabel.trim() || linkUrl.trim(), url: linkUrl.trim() }]);
    setLinkLabel(""); setLinkUrl("");
  };

  const save = async () => {
    if (!(form.first_name || form.last_name || form.company_name)) {
      toast.error(isPlace ? "Renseignez le nom du lieu" : "Renseignez au moins un nom");
      return;
    }
    if (dups.length > 0 && !dupIgnored && !contact) {
      toast.warning("Doublons potentiels détectés — confirmez en cliquant sur « Créer quand même »");
      setDupIgnored(true);
      return;
    }
    try {
      setSaving(true);
      const payload = { ...form };
      delete payload.id; delete payload.is_favorite; delete payload.projects_count;
      delete payload.created_by; delete payload.created_at; delete payload.updated_at;
      if (contact) {
        await updateDirectoryContact(contact.id, payload);
        toast.success("Fiche mise à jour");
      } else {
        await createDirectoryContact(payload);
        toast.success(isPlace ? "Lieu ajouté au répertoire" : "Contact ajouté au répertoire");
      }
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally { setSaving(false); }
  };

  const catOptions = categories.filter((c) => (isPlace ? c.is_location : true));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="contact-form-dialog">
        <DialogHeader>
          <DialogTitle>
            {contact ? "Modifier la fiche" : isPlace ? "Ajouter un lieu" : "Ajouter un contact"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Photo + noms */}
          <div className="flex gap-4">
            <button className="w-20 h-20 rounded-xl bg-slate-100 border border-dashed border-slate-300 flex items-center justify-center overflow-hidden shrink-0 hover:border-[#26788A]"
              onClick={() => photoRef.current?.click()} data-testid="contact-photo-btn">
              {form.photo ? <img src={form.photo} alt="" className="w-full h-full object-cover" /> : <ImagePlus size={20} className="text-slate-400" />}
            </button>
            <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={(e) => handlePhoto(e, false)} />
            <div className="flex-1 grid grid-cols-2 gap-3">
              {isPlace ? (
                <>
                  <div className="col-span-2">
                    <Label className="mb-1 block text-xs">Nom du lieu *</Label>
                    <Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} onBlur={checkDups} data-testid="contact-company-input" />
                  </div>
                  <div>
                    <Label className="mb-1 block text-xs">Type de lieu</Label>
                    <Select value={form.place_type || ""} onValueChange={(v) => set("place_type", v)}>
                      <SelectTrigger data-testid="place-type-select"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                      <SelectContent>{PLACE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label className="mb-1 block text-xs">Prénom</Label>
                    <Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} onBlur={checkDups} data-testid="contact-firstname-input" />
                  </div>
                  <div>
                    <Label className="mb-1 block text-xs">Nom</Label>
                    <Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} onBlur={checkDups} data-testid="contact-lastname-input" />
                  </div>
                  <div>
                    <Label className="mb-1 block text-xs">Entreprise (optionnel)</Label>
                    <Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="contact-company-input" />
                  </div>
                  <div>
                    <Label className="mb-1 block text-xs">Métier / spécialité</Label>
                    <Input value={form.job} onChange={(e) => set("job", e.target.value)} placeholder="Maquilleuse, comédien…" data-testid="contact-job-input" />
                  </div>
                </>
              )}
              <div className={isPlace ? "" : "col-span-2"}>
                <Label className="mb-1 block text-xs">Catégorie</Label>
                <Select value={form.category_id || ""} onValueChange={(v) => set("category_id", v)}>
                  <SelectTrigger data-testid="contact-category-select"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent className="max-h-[240px]">
                    {catOptions.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Alerte doublons */}
          {dups.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="duplicate-warning">
              <p className="text-xs font-semibold text-amber-800 flex items-center gap-1.5 mb-1">
                <AlertTriangle size={13} /> Doublon(s) potentiel(s) détecté(s)
              </p>
              {dups.map((d) => (
                <p key={d.id} className="text-xs text-amber-700">• {displayName(d)}{d.city ? ` (${d.city})` : ""} — {d.reasons.join(", ")}</p>
              ))}
            </div>
          )}

          {/* Coordonnées */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1 block text-xs">Téléphone</Label>
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} onBlur={checkDups} data-testid="contact-phone-input" />
            </div>
            <div>
              <Label className="mb-1 block text-xs">E-mail</Label>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} onBlur={checkDups} data-testid="contact-email-input" />
            </div>
            <div>
              <Label className="mb-1 block text-xs">Adresse</Label>
              <Input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="contact-address-input" />
            </div>
            <div>
              <Label className="mb-1 block text-xs">Ville</Label>
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Lille, Paris…" data-testid="contact-city-input" />
            </div>
            {isPlace ? (
              <>
                <div>
                  <Label className="mb-1 block text-xs">Contact sur place</Label>
                  <Input value={form.on_site_contact} onChange={(e) => set("on_site_contact", e.target.value)} data-testid="place-onsite-input" />
                </div>
                <div>
                  <Label className="mb-1 block text-xs">Capacité / infos utiles</Label>
                  <Input value={form.capacity_info} onChange={(e) => set("capacity_info", e.target.value)} placeholder="50 personnes, parking…" data-testid="place-capacity-input" />
                </div>
                <div className="col-span-2">
                  <Label className="mb-1 block text-xs">Contraintes éventuelles</Label>
                  <Input value={form.constraints} onChange={(e) => set("constraints", e.target.value)} placeholder="Bruit, horaires, autorisation…" data-testid="place-constraints-input" />
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label className="mb-1 block text-xs">Site internet / réseaux</Label>
                  <Input value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://… ou @instagram" data-testid="contact-website-input" />
                </div>
                <div>
                  <Label className="mb-1 block text-xs">Disponibilités</Label>
                  <Input value={form.availability} onChange={(e) => set("availability", e.target.value)} placeholder="Week-ends, soirées…" data-testid="contact-availability-input" />
                </div>
              </>
            )}
          </div>

          {/* Photos du lieu */}
          {isPlace && (
            <div>
              <Label className="mb-1.5 block text-xs">Photos du lieu</Label>
              <div className="flex flex-wrap gap-2">
                {(form.photos || []).map((p, i) => (
                  <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-slate-200">
                    <img src={p} alt="" className="w-full h-full object-cover" />
                    <button className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5"
                      onClick={() => set("photos", form.photos.filter((_, j) => j !== i))}><X size={10} /></button>
                  </div>
                ))}
                <button className="w-16 h-16 rounded-lg bg-slate-50 border border-dashed border-slate-300 flex items-center justify-center hover:border-[#26788A]"
                  onClick={() => placePhotosRef.current?.click()} data-testid="place-photos-btn">
                  <ImagePlus size={16} className="text-slate-400" />
                </button>
                <input ref={placePhotosRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handlePhoto(e, true)} />
              </div>
            </div>
          )}

          {/* Tags */}
          <div>
            <Label className="mb-1.5 block text-xs">Tags</Label>
            <div className="flex flex-wrap gap-1.5">
              {tags.map((t) => (
                <button key={t.id} onClick={() => toggleTag(t.name)} data-testid={`form-tag-${t.id}`}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${form.tags.includes(t.name) ? "bg-[#26788A] text-white border-[#26788A]" : "bg-white text-slate-600 border-slate-200 hover:border-[#26788A]/50"}`}>
                  {t.name}
                </button>
              ))}
              {isAdmin && (
                <span className="flex items-center gap-1">
                  <Input value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder="Nouveau tag" className="h-7 w-28 text-xs"
                    onKeyDown={(e) => e.key === "Enter" && addTag()} data-testid="new-tag-input" />
                  <Button size="sm" variant="outline" className="h-7 px-2" onClick={addTag} data-testid="add-tag-btn"><Plus size={12} /></Button>
                </span>
              )}
            </div>
          </div>

          {/* Liens utiles */}
          <div>
            <Label className="mb-1.5 block text-xs">Documents / liens utiles</Label>
            {(form.links || []).map((l, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-slate-600 mb-1">
                <a href={l.url} target="_blank" rel="noreferrer" className="text-[#26788A] underline truncate">{l.label}</a>
                <button className="text-slate-300 hover:text-red-500" onClick={() => set("links", form.links.filter((_, j) => j !== i))}><X size={12} /></button>
              </div>
            ))}
            <div className="flex gap-2">
              <Input value={linkLabel} onChange={(e) => setLinkLabel(e.target.value)} placeholder="Libellé" className="h-8 text-xs w-32" data-testid="link-label-input" />
              <Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://…" className="h-8 text-xs flex-1" data-testid="link-url-input" />
              <Button size="sm" variant="outline" className="h-8" onClick={addLink} data-testid="add-link-btn"><Plus size={13} /></Button>
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label className="mb-1 block text-xs">Notes internes</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="contact-notes-input" />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose}>Annuler</Button>
            <Button className="bg-[#26788A] hover:bg-[#1D5E6C] text-white" onClick={save} disabled={saving} data-testid="save-contact-btn">
              {saving && <Loader2 size={14} className="animate-spin mr-1.5" />}
              {dups.length > 0 && dupIgnored && !contact ? "Créer quand même" : contact ? "Enregistrer" : "Créer la fiche"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
